import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { OwnerEmptyScaffold } from '../kit/OwnerEmptyScaffold'
import { getEnvPillConfig, activeProjectId } from '../shell/ownerEnv'
import { useRoles } from '../context/RolesContext'
import { navigateOwner, ownerRouteToPath, type OwnerRoute } from '../ownerRouter'
import {
  getOwnerWorkspaceDetailV2,
  listDemoStories,
  listOwnerWorkspacesV2,
  type DemoStorySummary,
  type OwnerWorkspaceDetail,
  type OwnerWorkspaceDetailSection,
} from '../../../services/ownerWorkspacesService'
import type { OwnerActionInput, OwnerActionKind } from '../../../services/ownerConsoleService'
import { useSupportAccessReason } from '../shell/SupportAccessReasonDialog'
import { useNls } from '../../../nls/nlsProvider'
import { HosPatientLegalSetupCard } from './HosPatientLegalSetupCard'
import './ownerWorkspaces.css'

type DetailRoute = Extract<OwnerRoute, { view: 'workspace-detail' }>
type Tab = 'overview' | 'people' | 'money' | 'activity' | 'tickets' | 'ops' | 'config' | 'danger'
type PageSection = 'people' | 'tickets' | 'failedRuns' | 'audit' | 'activity'
const tabs: Array<{ id: Tab; key: 'NLSOwnerConsoleWorkspaceDetailOverview' | 'NLSOwnerConsoleWorkspaceDetailPeople' | 'NLSOwnerConsoleWorkspaceDetailMoney' | 'NLSOwnerConsoleWorkspaceDetailActivity' | 'NLSOwnerConsoleWorkspaceDetailTickets' | 'NLSOwnerConsoleWorkspaceDetailOps' | 'NLSOwnerConsoleWorkspaceDetailConfig' | 'NLSOwnerConsoleWorkspaceDetailDanger' }> = [
  { id: 'overview', key: 'NLSOwnerConsoleWorkspaceDetailOverview' }, { id: 'people', key: 'NLSOwnerConsoleWorkspaceDetailPeople' }, { id: 'money', key: 'NLSOwnerConsoleWorkspaceDetailMoney' }, { id: 'activity', key: 'NLSOwnerConsoleWorkspaceDetailActivity' }, { id: 'tickets', key: 'NLSOwnerConsoleWorkspaceDetailTickets' }, { id: 'ops', key: 'NLSOwnerConsoleWorkspaceDetailOps' }, { id: 'config', key: 'NLSOwnerConsoleWorkspaceDetailConfig' }, { id: 'danger', key: 'NLSOwnerConsoleWorkspaceDetailDanger' },
]
const validTab = (value: string | undefined): Tab => tabs.some((tab) => tab.id === value) ? value as Tab : 'overview'
const date = (value: string | null | undefined) => value ? new Date(value).toLocaleString('en-GB') : '—'
const number = (value: number | null | undefined) => value === null || value === undefined ? '—' : value.toLocaleString('en-IN')
const money = (value: number | null | undefined) => value === null || value === undefined ? '—' : `₹${value.toLocaleString('en-IN')}`
const storageGb = (bytes: number | null | undefined) => bytes === null || bytes === undefined ? '—' : `${(bytes / 1024 ** 3).toFixed(2)} GB`
const ACTIONS: Array<{ kind: OwnerActionKind; labelKey: 'NLSOwnerConsoleWorkspaceDetailGrantCredits' | 'NLSOwnerConsoleWorkspaceDetailChangePlan' | 'NLSOwnerConsoleWorkspaceDetailSuspend' | 'NLSOwnerConsoleWorkspaceDetailRefreshUsage' | 'NLSOwnerConsoleWorkspaceDetailRetry' | 'NLSOwnerConsoleWorkspaceDetailInspect' | 'NLSOwnerConsoleWorkspaceDetailResetContent' | 'NLSOwnerConsoleWorkspaceDetailResetChat' | 'NLSOwnerConsoleWorkspaceDetailResetDesign' | 'NLSOwnerConsoleWorkspaceDetailDemoUnlock' | 'NLSOwnerConsoleWorkspaceDemoStoryActionSave' | 'NLSOwnerConsoleWorkspaceDemoStoryActionRestore' | 'NLSOwnerConsoleWorkspaceDemoStoryActionDelete'; roles: Array<'founder' | 'support' | 'finance' | 'readonly'>; preview?: boolean; destructive?: boolean; /** Confirm by typing the workspace name without the danger styling. */ requiresName?: boolean; /** Run from its own card, not the generic action list. */ card?: boolean }> = [
  { kind: 'GRANT_WORKSPACE_CREDITS', labelKey: 'NLSOwnerConsoleWorkspaceDetailGrantCredits', roles: ['founder', 'support', 'finance'], preview: true }, { kind: 'CHANGE_PLAN', labelKey: 'NLSOwnerConsoleWorkspaceDetailChangePlan', roles: ['founder', 'finance'], preview: true }, { kind: 'SUSPEND_WORKSPACE', labelKey: 'NLSOwnerConsoleWorkspaceDetailSuspend', roles: ['founder', 'support', 'finance'] }, { kind: 'FORCE_USAGE_REFRESH', labelKey: 'NLSOwnerConsoleWorkspaceDetailRefreshUsage', roles: ['founder', 'support', 'finance', 'readonly'] }, { kind: 'RETRY_FAILED_JOB', labelKey: 'NLSOwnerConsoleWorkspaceDetailRetry', roles: ['founder', 'support', 'finance'] }, { kind: 'INSPECT_WORKSPACE', labelKey: 'NLSOwnerConsoleWorkspaceDetailInspect', roles: ['founder', 'support', 'finance', 'readonly'] },
  { kind: 'SET_WORKSPACE_DEMO_UNLOCK', labelKey: 'NLSOwnerConsoleWorkspaceDetailDemoUnlock', roles: ['founder'], preview: true, requiresName: true },
  { kind: 'SAVE_DEMO_STORY', labelKey: 'NLSOwnerConsoleWorkspaceDemoStoryActionSave', roles: ['founder'], card: true },
  { kind: 'RESTORE_DEMO_STORY', labelKey: 'NLSOwnerConsoleWorkspaceDemoStoryActionRestore', roles: ['founder'], preview: true, requiresName: true, card: true },
  { kind: 'DELETE_DEMO_STORY', labelKey: 'NLSOwnerConsoleWorkspaceDemoStoryActionDelete', roles: ['founder'], requiresName: true, card: true },
  { kind: 'RESET_WORKSPACE_CONTENT', labelKey: 'NLSOwnerConsoleWorkspaceDetailResetContent', roles: ['founder'], preview: true, destructive: true }, { kind: 'RESET_CHAT_FOR_WORKSPACE', labelKey: 'NLSOwnerConsoleWorkspaceDetailResetChat', roles: ['founder'], preview: true, destructive: true }, { kind: 'RESET_DESIGN_HUB_STALE', labelKey: 'NLSOwnerConsoleWorkspaceDetailResetDesign', roles: ['founder'], preview: true, destructive: true },
]
const Rows = <T extends { id: string }>({ value, empty, render, nextLabel, onNext }: { value: OwnerWorkspaceDetailSection<T>; empty: string; render: (item: T) => ReactNode; nextLabel: string; onNext?: (cursor: string) => void }) => value.state === 'unavailable' ? <p className="ownerWorkspaceUnavailable">{value.message}</p> : <>{value.items.length ? <ul className="ownerWorkspaceDetailRows">{value.items.map((item) => <li key={item.id}>{render(item)}</li>)}</ul> : <p className="ownerWorkspaceEmpty">{empty}</p>}{value.nextCursor && onNext ? <button type="button" onClick={() => onNext(value.nextCursor!)}>{nextLabel}</button> : null}</>

type SectionCache =
  | { state: 'available'; items: Array<{ id: string } & Record<string, unknown>>; nextCursor: string | null }
  | { state: 'unavailable'; message: string }

const storySize = (bytes: number) => bytes >= 1024 ** 2 ? `${(bytes / 1024 ** 2).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
const storyDocs = (value: unknown) => typeof value === 'number' ? value : 0

const emptySectionCache = (): Record<PageSection, SectionCache> => ({
  people: { state: 'available', items: [], nextCursor: null },
  tickets: { state: 'available', items: [], nextCursor: null },
  failedRuns: { state: 'available', items: [], nextCursor: null },
  audit: { state: 'available', items: [], nextCursor: null },
  activity: { state: 'available', items: [], nextCursor: null },
})

export function OwnerWorkspaceDetailPanel({
  route,
  onSubmit,
  isAside = false,
  onClose,
  onSelectWorkspace,
}: {
  route: DetailRoute
  onSubmit: (input: OwnerActionInput) => Promise<{ ok: true; data?: Record<string, unknown> } | { ok: false; message: string }>
  isAside?: boolean
  onClose?: () => void
  onSelectWorkspace?: (workspaceId: string) => void
}) {
  const { t } = useNls(); const { role } = useRoles(); const { ensureAccessReason } = useSupportAccessReason(); const [detail, setDetail] = useState<OwnerWorkspaceDetail | null>(null); const [loading, setLoading] = useState(true); const [loadError, setLoadError] = useState<string | null>(null)
  const ensureAccessReasonRef = useRef(ensureAccessReason)
  useEffect(() => {
    ensureAccessReasonRef.current = ensureAccessReason
  }, [ensureAccessReason])
  const isAsideRef = useRef(isAside)
  useEffect(() => {
    isAsideRef.current = isAside
  }, [isAside])
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])
  const queryTicketId = typeof route.query?.ticketId === 'string' ? route.query.ticketId : ''
  const queryTicketIdRef = useRef(queryTicketId)
  useEffect(() => {
    queryTicketIdRef.current = queryTicketId
  }, [queryTicketId])
  const tRef = useRef(t)
  useEffect(() => {
    tRef.current = t
  })
  const [action, setAction] = useState<OwnerActionKind | null>(null); const [reason, setReason] = useState('DATA_CORRECTION'); const [credits, setCredits] = useState('25'); const [plan, setPlan] = useState('FREE_TIER'); const [suspended, setSuspended] = useState(true); const [runId, setRunId] = useState(''); const [confirm, setConfirm] = useState(''); const [preview, setPreview] = useState<Record<string, unknown> | null>(null); const [actionError, setActionError] = useState<string | null>(null); const [actionResult, setActionResult] = useState<Record<string, unknown> | null>(null)
  const [previewPending, setPreviewPending] = useState(false)
  const [storyName, setStoryName] = useState('')
  const [storyId, setStoryId] = useState('')
  const [storyNameError, setStoryNameError] = useState<string | null>(null)
  const [storiesData, setStoriesData] = useState<{ workspaceId: string; items: DemoStorySummary[] | null; failed: boolean } | null>(null)
  const storiesVersion = useRef(0)
  const [mutationPending, setMutationPending] = useState(false)
  const inFlightMutationRef = useRef(false)
  const previewRequestId = useRef(0)
  const actionVersion = useRef(0)
  const [sectionPages, setSectionPages] = useState<Record<PageSection, SectionCache>>(emptySectionCache)
  const [pickerQuery, setPickerQuery] = useState('')
  const [pickerResults, setPickerResults] = useState<Array<{ id: string; name: string; plan: string }>>([])
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [asideTab, setAsideTab] = useState<Tab>(validTab(route.query?.tab))
  const detailVersion = useRef(0); const routeWorkspaceRef = useRef(route.workspaceId); const loadedWorkspaceRef = useRef<string | null>(null); const cancelRef = useRef<HTMLButtonElement>(null); const workspaceId = route.workspaceId; const tab = isAside ? asideTab : validTab(route.query?.tab); const selected = ACTIONS.find((entry) => entry.kind === action) || null; const environment = getEnvPillConfig(activeProjectId()).label

  const [prevPickerQuery, setPrevPickerQuery] = useState(pickerQuery)
  if (prevPickerQuery !== pickerQuery) {
    setPrevPickerQuery(pickerQuery)
    if (!pickerQuery.trim()) {
      setPickerResults([])
    }
  }

  useEffect(() => {
    if (!pickerQuery.trim()) {
      return
    }
    let active = true
    const timer = setTimeout(async () => {
      try {
        const res = await listOwnerWorkspacesV2({ search: pickerQuery.trim(), limit: 10 })
        if (active) setPickerResults(res.workspaces.map((w) => ({ id: w.id, name: w.name, plan: w.plan })))
      } catch {
        if (active) setPickerResults([])
      }
    }, 200)
    return () => { active = false; clearTimeout(timer) }
  }, [pickerQuery])

  const invalidateAction = () => {
    actionVersion.current += 1
    previewRequestId.current += 1
    setPreview(null)
    setPreviewPending(false)
    setActionError(null)
  }

  const load = useCallback(async (page?: { section?: PageSection; cursor?: string | null }) => {
    const reason = await ensureAccessReasonRef.current({
      key: `WORKSPACE:${workspaceId}`,
      defaultCode: 'SUPPORT_TICKET',
      defaultTicketId: queryTicketIdRef.current,
      onCancel: () => {
        if (isAsideRef.current && onCloseRef.current) onCloseRef.current()
        else if (typeof window !== 'undefined' && window.history.length > 1) window.history.back()
        else navigateOwner({ view: 'workspaces' })
      },
    })
    if (!reason) {
      setLoading(false)
      return
    }
    const current = ++detailVersion.current; setLoading(true); setLoadError(null)
    try {
      const result = await getOwnerWorkspaceDetailV2(workspaceId, page, reason)
      if (current !== detailVersion.current || routeWorkspaceRef.current !== workspaceId) return
      loadedWorkspaceRef.current = result.workspace.id
      if (page?.section) {
        const section = page.section
        setDetail((prev) => (prev ? { ...prev, [section]: result[section] } : result))
        const incoming = result[section]
        if (incoming.state === 'available') {
          setSectionPages((prev) => {
            const prevItems = prev[section]?.state === 'available' ? prev[section].items : []
            return {
              ...prev,
              [section]: {
                state: 'available',
                items: [...prevItems, ...incoming.items],
                nextCursor: incoming.nextCursor,
              },
            }
          })
        } else {
          setSectionPages((prev) => ({
            ...prev,
            [section]: { state: 'unavailable', message: incoming.message },
          }))
        }
      } else {
        setDetail(result)
        const fresh = emptySectionCache()
        for (const key of Object.keys(fresh) as PageSection[]) {
          const s = result[key]
          if (s.state === 'available') {
            fresh[key] = { state: 'available', items: s.items, nextCursor: s.nextCursor }
          } else {
            fresh[key] = { state: 'unavailable', message: s.message }
          }
        }
        setSectionPages(fresh)
      }
    } catch (error: unknown) {
      if (current === detailVersion.current && routeWorkspaceRef.current === workspaceId) {
        if (page?.section) {
          const section = page.section
          const message = error instanceof Error ? error.message : tRef.current('NLSOwnerConsoleWorkspaceSectionUnavailable')
          setSectionPages((prev) => ({ ...prev, [section]: { state: 'unavailable', message } }))
          setDetail((prev) => (prev ? { ...prev, [section]: { state: 'unavailable', message } } : null))
        } else {
          setLoadError(error instanceof Error ? error.message : tRef.current('NLSOwnerConsoleWorkspaceDetailUnavailable'))
        }
      }
    } finally {
      if (current === detailVersion.current && routeWorkspaceRef.current === workspaceId) setLoading(false)
    }
  }, [workspaceId])

  const [prevWorkspaceId, setPrevWorkspaceId] = useState(workspaceId)
  if (prevWorkspaceId !== workspaceId) {
    setPrevWorkspaceId(workspaceId)
    setDetail(null)
    setSectionPages(emptySectionCache())
    setLoading(true)
    setMutationPending(false)
    setStoryId('')
    setStoryName('')
    setStoryNameError(null)
    setAction(null)
    setActionResult(null)
    setConfirm('')
    setRunId('')
    setPreview(null)
    setPreviewPending(false)
    setActionError(null)
  }

  useEffect(() => {
    routeWorkspaceRef.current = workspaceId
    const current = ++detailVersion.current
    loadedWorkspaceRef.current = null
    actionVersion.current += 1
    previewRequestId.current += 1
    inFlightMutationRef.current = false
    let cancelled = false
    void (async () => {
      const reason = await ensureAccessReasonRef.current({
        key: `WORKSPACE:${workspaceId}`,
        defaultCode: 'SUPPORT_TICKET',
        defaultTicketId: queryTicketIdRef.current,
        onCancel: () => {
          if (isAsideRef.current && onCloseRef.current) onCloseRef.current()
          else if (typeof window !== 'undefined' && window.history.length > 1) window.history.back()
          else navigateOwner({ view: 'workspaces' })
        },
      })
      if (!reason || cancelled || current !== detailVersion.current) {
        if (!reason) setLoading(false)
        return
      }
      try {
        const result = await getOwnerWorkspaceDetailV2(workspaceId, undefined, reason)
        if (cancelled || current !== detailVersion.current) return
        loadedWorkspaceRef.current = result.workspace.id
        setDetail(result)
        const fresh = emptySectionCache()
        for (const key of Object.keys(fresh) as PageSection[]) {
          const s = result[key]
          if (s.state === 'available') {
            fresh[key] = { state: 'available', items: s.items, nextCursor: s.nextCursor }
          } else {
            fresh[key] = { state: 'unavailable', message: s.message }
          }
        }
        setSectionPages(fresh)
        setLoading(false)
      } catch (error: unknown) {
        if (cancelled || current !== detailVersion.current) return
        setLoadError(error instanceof Error ? error.message : tRef.current('NLSOwnerConsoleWorkspaceDetailUnavailable'))
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [workspaceId])

  useEffect(() => { if (action) setTimeout(() => cancelRef.current?.focus(), 0) }, [action])

  const loadStories = useCallback(async () => {
    const reason = await ensureAccessReasonRef.current({
      key: `WORKSPACE:${workspaceId}`,
      defaultCode: 'SUPPORT_TICKET',
      defaultTicketId: queryTicketIdRef.current,
    })
    if (!reason) return
    const version = ++storiesVersion.current
    try {
      const result = await listDemoStories(workspaceId, reason)
      if (version !== storiesVersion.current) return
      setStoriesData({ workspaceId, items: result, failed: false })
    } catch {
      if (version !== storiesVersion.current) return
      setStoriesData({ workspaceId, items: null, failed: true })
    }
  }, [workspaceId])

  const storyCardActive = tab === 'config' && role === 'founder' && detail?.workspace.id === workspaceId
  useEffect(() => {
    if (!storyCardActive) return
    // Deferred so the load starts after the effect (loadStories guards against stale results itself).
    queueMicrotask(() => { void loadStories() })
  }, [storyCardActive, loadStories])

  const closeDrawer = () => {
    if (inFlightMutationRef.current) return
    invalidateAction()
    setAction(null)
    setConfirm('')
  }

  const changeAction = (kind: OwnerActionKind) => {
    if (inFlightMutationRef.current) return
    invalidateAction()
    setAction(kind)
    setActionResult(null)
    setConfirm('')
  }

  const changeParam = <T,>(setter: (value: T) => void, value: T, affectsPreview = true) => {
    if (inFlightMutationRef.current) return
    if (affectsPreview) {
      previewRequestId.current += 1
      setPreview(null)
      setPreviewPending(false)
      setActionError(null)
    } else {
      setActionError(null)
    }
    setter(value)
  }

  const goBack = () => {
    try {
      const value = route.query?.returnQuery ? JSON.parse(decodeURIComponent(route.query.returnQuery)) : {}
      navigateOwner({ view: 'workspaces', query: value && typeof value === 'object' ? value : {} })
    } catch {
      navigateOwner({ view: 'workspaces' })
    }
  }

  const nextPage = (section: PageSection, cursor: string) => void load({ section, cursor })

  const demoUnlockEnabled = detail?.workspace.demoUnlock?.enabled === true

  const run = async (dryRun: boolean) => {
    if (!detail || !selected || detail.workspace.id !== workspaceId || loadedWorkspaceRef.current !== workspaceId) return
    if (inFlightMutationRef.current) return

    if (dryRun) {
      if (previewPending) return
      const reqId = ++previewRequestId.current
      const requestedWorkspaceId = workspaceId
      const currentAction = selected.kind
      setPreviewPending(true)
      setActionError(null)
      const input: OwnerActionInput = {
        kind: selected.kind,
        workspaceId: requestedWorkspaceId,
        reason,
        dryRun: true,
        creditAmount: selected.kind === 'GRANT_WORKSPACE_CREDITS' ? Number(credits) : undefined,
        plan: selected.kind === 'CHANGE_PLAN' ? plan : undefined,
        enabled: selected.kind === 'SUSPEND_WORKSPACE' ? suspended : selected.kind === 'SET_WORKSPACE_DEMO_UNLOCK' ? !demoUnlockEnabled : undefined,
        runId: selected.kind === 'RETRY_FAILED_JOB' ? runId : undefined,
        storyId: selected.kind === 'RESTORE_DEMO_STORY' || selected.kind === 'DELETE_DEMO_STORY' ? storyId : undefined,
      }
      try {
        const result = await onSubmit(input)
        if (
          reqId !== previewRequestId.current ||
          routeWorkspaceRef.current !== requestedWorkspaceId ||
          loadedWorkspaceRef.current !== requestedWorkspaceId ||
          action !== currentAction
        ) return
        if (!result.ok) {
          setActionError(result.message)
          return
        }
        setPreview(result.data || {})
      } catch (error: unknown) {
        if (reqId === previewRequestId.current && routeWorkspaceRef.current === requestedWorkspaceId) {
          setActionError(error instanceof Error ? error.message : t('NLSOwnerConsoleWorkspaceDetailUnavailable'))
        }
      } finally {
        if (reqId === previewRequestId.current) setPreviewPending(false)
      }
    } else {
      if (selected.preview && !preview?.previewToken) {
        setActionError(t('NLSOwnerConsoleWorkspaceDetailPreviewRequired'))
        return
      }
      if ((selected.destructive || selected.requiresName) && confirm !== detail.workspace.name) {
        setActionError(t('NLSOwnerConsoleWorkspaceDetailTypeName'))
        return
      }
      inFlightMutationRef.current = true
      setMutationPending(true)
      setActionError(null)
      const actVer = ++actionVersion.current
      const requestedWorkspaceId = workspaceId
      const currentAction = selected.kind
      const input: OwnerActionInput = {
        kind: selected.kind,
        workspaceId: requestedWorkspaceId,
        reason,
        dryRun: false,
        creditAmount: selected.kind === 'GRANT_WORKSPACE_CREDITS' ? Number(credits) : undefined,
        plan: selected.kind === 'CHANGE_PLAN' ? plan : undefined,
        enabled: selected.kind === 'SUSPEND_WORKSPACE' ? suspended : selected.kind === 'SET_WORKSPACE_DEMO_UNLOCK' ? !demoUnlockEnabled : undefined,
        runId: selected.kind === 'RETRY_FAILED_JOB' ? runId : undefined,
        storyId: selected.kind === 'RESTORE_DEMO_STORY' || selected.kind === 'DELETE_DEMO_STORY' ? storyId : undefined,
        storyName: selected.kind === 'SAVE_DEMO_STORY' ? storyName.trim() : undefined,
        confirmPhrase: selected.destructive || selected.requiresName ? confirm : undefined,
        previewToken: typeof preview?.previewToken === 'string' ? preview.previewToken : undefined,
      }
      try {
        const result = await onSubmit(input)
        const isCurrent =
          actVer === actionVersion.current &&
          routeWorkspaceRef.current !== null &&
          routeWorkspaceRef.current === requestedWorkspaceId &&
          loadedWorkspaceRef.current === requestedWorkspaceId &&
          action === currentAction
        if (!isCurrent) return
        if (!result.ok) {
          setActionError(result.message)
          return
        }
        setActionResult(result.data || {})
        setPreview(null)
        void load()
        if (role === 'founder') void loadStories()
      } catch (error: unknown) {
        const isCurrent =
          actVer === actionVersion.current &&
          routeWorkspaceRef.current !== null &&
          routeWorkspaceRef.current === requestedWorkspaceId &&
          loadedWorkspaceRef.current === requestedWorkspaceId &&
          action === currentAction
        if (isCurrent) setActionError(error instanceof Error ? error.message : t('NLSOwnerConsoleWorkspaceDetailUnavailable'))
      } finally {
        if (actVer === actionVersion.current) {
          inFlightMutationRef.current = false
          setMutationPending(false)
        }
      }
    }
  }

  const nextLabel = t('NLSOwnerConsoleWorkspaceDetailNextPage')
  if (loading && !detail) return <section className="ownerWorkspacesContainer"><p>{t('NLSOwnerConsoleWorkspaceDetailLoading')}</p></section>
  if (!detail || detail.workspace.id !== workspaceId || loadError) return <OwnerEmptyScaffold title={t('NLSOwnerConsoleWorkspaceDetailUnavailableTitle')} body={loadError || t('NLSOwnerConsoleWorkspaceDetailUnavailable')} />
  const workspace = detail.workspace
  const normalActions = ACTIONS.filter((entry) => !entry.destructive && !entry.card && entry.roles.includes(role))
  const dangerActions = ACTIONS.filter((entry) => entry.destructive && entry.roles.includes(role))
  const selectedLabel = selected ? t(selected.labelKey) : ''
  const isFounder = role === 'founder'
  const storyBusy = mutationPending || previewPending
  const stories = storiesData?.workspaceId === workspaceId ? storiesData.items : null
  const storiesFailed = storiesData?.workspaceId === workspaceId && storiesData.failed
  const selectedStory = stories?.find((story) => story.storyId === storyId) || null
  const openStoryAction = (kind: OwnerActionKind, story?: DemoStorySummary) => {
    if (inFlightMutationRef.current) return
    if (kind === 'SAVE_DEMO_STORY') {
      if (!storyName.trim()) { setStoryNameError(t('NLSOwnerConsoleWorkspaceDemoStoryNameRequired')); return }
      setStoryNameError(null)
    }
    changeAction(kind)
    setStoryId(story?.storyId ?? '')
  }

  const sectionView = <T,>(section: PageSection, initial: OwnerWorkspaceDetailSection<T>): OwnerWorkspaceDetailSection<T> => {
    const cached = sectionPages[section]
    if (cached) return cached as OwnerWorkspaceDetailSection<T>
    return initial
  }

  return (
    <section className="ownerWorkspacesContainer ownerWorkspaceDetail" data-testid="owner-workspace-detail">
      <header className="ownerWorkspaceDetailHeader">
        <div>
          <button type="button" onClick={isAside && onClose ? onClose : goBack}>
            {isAside ? t('NLSOwnerConsoleWorkspaceAsideClose') : t('NLSOwnerConsoleWorkspaceDetailBack')}
          </button>
          {isAside && (
            <a
              href={ownerRouteToPath({ view: 'workspace-detail', workspaceId: workspace.id, query: { ...route.query, tab } })}
              style={{ marginLeft: 8, fontSize: 13, textDecoration: 'underline', color: 'var(--text-accent)' }}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
                e.preventDefault()
                navigateOwner({ view: 'workspace-detail', workspaceId: workspace.id, query: { ...route.query, tab } })
              }}
            >
              {t('NLSOwnerConsoleWorkspaceAsideOpenFull')}
            </a>
          )}
          <h1>{workspace.name}</h1>
          <p>{workspace.type} · {workspace.plan} · {workspace.status}</p>
          <p>{workspace.owners && workspace.owners.length > 1 ? t('NLSOwnerConsoleWorkspaceDetailOwners') : t('NLSOwnerConsoleWorkspaceDetailOwner')}: {workspace.owners && workspace.owners.length > 1 ? workspace.owners.map((o) => o.email || o.displayName || o.uid).join(', ') : (workspace.ownerEmail || '—')} · {t('NLSOwnerConsoleWorkspaceDetailId')}: <code>{workspace.id}</code></p>
        </div>
        <aside>
          <strong>{environment}</strong>
          <div className="ownerWorkspacePicker">
            <input
              type="search"
              placeholder={t('NLSOwnerConsoleWorkspaceSearchPlaceholder')}
              value={pickerQuery}
              onChange={(e) => setPickerQuery(e.target.value)}
              onFocus={() => setIsPickerOpen(true)}
              aria-label={t('NLSOwnerConsoleWorkspaceSearchLabel')}
            />
            {isPickerOpen && pickerResults.length > 0 && (
              <ul className="ownerWorkspacePickerDropdown">
                {pickerResults.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsPickerOpen(false)
                        setPickerQuery('')
                        if (isAside && onSelectWorkspace) {
                          onSelectWorkspace(item.id)
                        } else {
                          navigateOwner({ view: 'workspace-detail', workspaceId: item.id, query: { ...route.query, tab } })
                        }
                      }}
                    >
                      <strong>{item.name}</strong> <code>{item.id}</code> ({item.plan})
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </header>

      <nav className="ownerWorkspaceTabs" aria-label={t('NLSOwnerConsoleWorkspaceTitle')}>
        {tabs.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={tab === entry.id ? 'ownerWorkspaceView--active' : ''}
            onClick={() => {
              if (isAside) {
                setAsideTab(entry.id)
              } else {
                navigateOwner({ view: 'workspace-detail', workspaceId, query: { ...route.query, tab: entry.id } })
              }
            }}
          >
            {t(entry.key)}
          </button>
        ))}
      </nav>

      {tab === 'overview' && (
        <div className="ownerWorkspaceDetailGrid">
          <article>
            <h2>{t('NLSOwnerConsoleWorkspaceDetailOverview')}</h2>
            <p>{t('NLSOwnerConsoleWorkspaceDetailPeople')}: {number(workspace.summary?.activeMemberCount)}</p>
            <p>{t('NLSOwnerConsoleWorkspaceDetailCredits')}: {number(workspace.wallet.state === 'available' ? workspace.wallet.availableCredits : null)}</p>
            <p>{t('NLSOwnerConsoleWorkspaceDetailRevenue')}: {money(workspace.summary?.mrrModeled)} · {t('NLSOwnerConsoleWorkspaceDetailCost')}: {money(workspace.summary?.estimatedCost)} · {t('NLSOwnerConsoleWorkspaceDetailMargin')}: {number(workspace.summary?.marginModeled)} · {t('NLSOwnerConsoleWorkspaceDetailRisk')}: {workspace.summary?.riskTier || '—'}</p>
            <div style={{ marginTop: 12, borderTop: '1px solid var(--divider)', paddingTop: 8 }}>
              <p style={{ margin: '4px 0' }}>
                {t('NLSOwnerConsoleWorkspaceOverviewSignals', {
                  tickets: number(workspace.summary?.openTicketCount),
                  jobs: number(workspace.summary?.failedJobsCount),
                  risks: number(workspace.summary?.openRiskCount),
                })}
              </p>
            </div>
            <div style={{ marginTop: 12, borderTop: '1px solid var(--divider)', paddingTop: 8 }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 'var(--fs-small, 13px)', color: 'var(--text-mid)' }}>{t('NLSOwnerConsoleWorkspaceAlerts')}</h3>
              {workspace.alertsState === 'unavailable' ? (
                <p style={{ margin: '4px 0', color: 'var(--text-low)', fontSize: 'var(--fs-tiny, 12px)' }}>{t('NLSOwnerConsoleWorkspaceAlertsUnavailable')}</p>
              ) : workspace.alertsState === 'unknown' ? (
                <p style={{ margin: '4px 0', color: 'var(--text-low)', fontSize: 'var(--fs-tiny, 12px)' }}>{t('NLSOwnerConsoleWorkspaceAlertsUnknown')}</p>
              ) : workspace.alertsState === 'partial' ? (
                <>
                  {workspace.alerts && workspace.alerts.length > 0 ? (
                    <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
                      {workspace.alerts.map((alert) => (
                        <li key={alert.id} style={{ margin: '2px 0' }}>
                          <span className="ownerBadge" style={{ margin: 0 }}>{alert.message}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <p style={{ margin: '4px 0', color: 'var(--text-low)', fontSize: 'var(--fs-tiny, 12px)' }}>{t('NLSOwnerConsoleWorkspaceAlertsPartial')}</p>
                </>
              ) : workspace.alertsState === 'available' ? (
                workspace.alerts && workspace.alerts.length > 0 ? (
                  <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
                    {workspace.alerts.map((alert) => (
                      <li key={alert.id} style={{ margin: '2px 0' }}>
                        <span className="ownerBadge" style={{ margin: 0 }}>{alert.message}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: '4px 0', color: 'var(--text-low)', fontSize: 'var(--fs-tiny, 12px)' }}>{t('NLSOwnerConsoleWorkspaceNoAlerts')}</p>
                )
              ) : (
                <p style={{ margin: '4px 0', color: 'var(--text-low)', fontSize: 'var(--fs-tiny, 12px)' }}>{t('NLSOwnerConsoleWorkspaceAlertsUnknown')}</p>
              )}
            </div>
            <div style={{ marginTop: 12, borderTop: '1px solid var(--divider)', paddingTop: 8 }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 'var(--fs-small, 13px)', color: 'var(--text-mid)' }}>{t('NLSOwnerConsoleWorkspaceModuleSignals')}</h3>
              {workspace.moduleSignals?.state === 'available' ? (
                <ul style={{ margin: '4px 0', paddingLeft: 0, listStyle: 'none' }}>
                  {workspace.moduleSignals.items.map((item) => {
                    const stateLabel = item.state === 'observed' ? t('NLSOwnerConsoleWorkspaceSignalObserved')
                      : item.state === 'zero' ? t('NLSOwnerConsoleWorkspaceSignalZero')
                      : item.state === 'unavailable' ? t('NLSOwnerConsoleWorkspaceSignalUnavailable')
                      : t('NLSOwnerConsoleWorkspaceSignalUnknown')
                    return (
                      <li key={item.key} style={{ margin: '3px 0', display: 'flex', gap: 6, alignItems: 'center' }}>
                        <strong style={{ fontSize: 'var(--fs-small, 13px)' }}>{item.label}:</strong>
                        <span style={{ color: 'var(--text-mid)', fontSize: 'var(--fs-tiny, 12px)' }}>{stateLabel}</span>
                        {item.currentUsage !== null && (
                          <small style={{ color: 'var(--text-low)' }}>({item.currentUsage})</small>
                        )}
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <p style={{ margin: '4px 0', color: 'var(--text-low)', fontSize: 'var(--fs-tiny, 12px)' }}>{t('NLSOwnerConsoleWorkspaceModuleSignalsUnavailable')}</p>
              )}
            </div>
          </article>
          <article>
            <h2>{t('NLSOwnerConsoleWorkspaceDetailTickets')}</h2>
            <Rows
              value={sectionView('tickets', detail.tickets)}
              empty={t('NLSOwnerConsoleWorkspaceDetailNoRows')}
              render={(item) => (
                <button type="button" onClick={() => navigateOwner({ view: 'ticket-detail', ticketId: item.id })}>
                  {item.id} · {item.status}
                </button>
              )}
              nextLabel={nextLabel}
              onNext={(cursor) => nextPage('tickets', cursor)}
            />
          </article>
          <article>
            <h2>{t('NLSOwnerConsoleWorkspaceDetailOwnerAudit')}</h2>
            <Rows
              value={sectionView('audit', detail.audit)}
              empty={t('NLSOwnerConsoleWorkspaceDetailNoRows')}
              render={(item) => <span>{item.action} · {date(item.createdAt)}</span>}
              nextLabel={nextLabel}
              onNext={(cursor) => nextPage('audit', cursor)}
            />
          </article>
        </div>
      )}

      {tab === 'people' && (
        <article>
          <h2>{t('NLSOwnerConsoleWorkspaceDetailPeople')}</h2>
          <Rows
            value={sectionView('people', detail.people)}
            empty={t('NLSOwnerConsoleWorkspaceDetailNoRows')}
            render={(item) => (
              <button
                type="button"
                onClick={() => item.userId && navigateOwner({ view: 'person-detail', userId: item.userId })}
              >
                <span>{item.displayName || item.email || (item.userId ? item.userId.slice(0, 16) + (item.userId.length > 16 ? '…' : '') : '—')}</span>
                {item.isOwner && <span className="ownerBadge">{t('NLSOwnerConsoleWorkspaceOwnerBadge')}</span>}
                {item.email && item.displayName && <span> ({item.email})</span>}
                <span> · {item.role} · {date(item.createdAt)}</span>
              </button>
            )}
            nextLabel={nextLabel}
            onNext={(cursor) => nextPage('people', cursor)}
          />
        </article>
      )}

      {tab === 'money' && (
        <article>
          <h2>{t('NLSOwnerConsoleWorkspaceDetailMoney')}</h2>
          <p>{t('NLSOwnerConsoleWorkspaceDetailCredits')}: {number(workspace.wallet.state === 'available' ? workspace.wallet.availableCredits : null)}</p>
          <p>{t('NLSOwnerConsoleWorkspaceDetailStorage')}: {storageGb(workspace.summary?.storageUsedBytes)} / {storageGb(workspace.summary?.storagePlanBytes)}</p>
          {(() => {
            const used = workspace.summary?.storageUsedBytes ?? 0
            const quota = workspace.summary?.storagePlanBytes ?? 0
            const pct = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0
            return (
              <div className="ownerWorkspaceStorage" title={`${storageGb(used)} / ${storageGb(quota)} (${pct}%)`} style={{ maxWidth: 300, margin: '8px 0' }}>
                <i><b style={{ width: `${pct}%` }} /></i>
              </div>
            )
          })()}
        </article>
      )}

      {tab === 'activity' && (
        <article>
          <h2>{t('NLSOwnerConsoleWorkspaceDetailActivity')}</h2>
          <Rows
            value={sectionView('activity', detail.activity)}
            empty={t('NLSOwnerConsoleWorkspaceDetailNoRows')}
            render={(item) => <span>{item.action} · {date(item.createdAt)}</span>}
            nextLabel={nextLabel}
            onNext={(cursor) => nextPage('activity', cursor)}
          />
        </article>
      )}

      {tab === 'tickets' && (
        <article>
          <h2>{t('NLSOwnerConsoleWorkspaceDetailTickets')}</h2>
          <Rows
            value={sectionView('tickets', detail.tickets)}
            empty={t('NLSOwnerConsoleWorkspaceDetailNoRows')}
            render={(item) => (
              <button type="button" onClick={() => navigateOwner({ view: 'ticket-detail', ticketId: item.id })}>
                {item.id} · {item.status}
              </button>
            )}
            nextLabel={nextLabel}
            onNext={(cursor) => nextPage('tickets', cursor)}
          />
        </article>
      )}

      {tab === 'ops' && (
        <article>
          <h2>{t('NLSOwnerConsoleWorkspaceDetailOps')}</h2>
          <Rows
            value={sectionView('failedRuns', detail.failedRuns)}
            empty={t('NLSOwnerConsoleWorkspaceDetailNoRows')}
            render={(item) => ACTIONS.find((entry) => entry.kind === 'RETRY_FAILED_JOB')?.roles.includes(role) ? (
              <button
                type="button"
                onClick={() => {
                  changeParam(setRunId, item.id)
                  changeAction('RETRY_FAILED_JOB')
                }}
              >
                {item.id} · {item.workflowId || '—'}
              </button>
            ) : (
              <span>{item.id} · {item.workflowId || '—'}</span>
            )}
            nextLabel={nextLabel}
            onNext={(cursor) => nextPage('failedRuns', cursor)}
          />
        </article>
      )}

      {tab === 'config' && (
        <article>
          <h2>{t('NLSOwnerConsoleWorkspaceDetailConfig')}</h2>
          <div style={{ display: 'grid', gap: 4, marginBottom: 16 }}>
            <p>{t('NLSOwnerConsoleWorkspaceDetailPlan')}: {workspace.plan}</p>
            <p>{t('NLSOwnerConsoleWorkspaceConfigType', { type: workspace.type })}</p>
            <p>{t('NLSOwnerConsoleWorkspaceConfigStatus', { status: workspace.status })}</p>
            <p>{t('NLSOwnerConsoleWorkspaceConfigProjection', { state: workspace.projectionState })}</p>
            <p>{t('NLSOwnerConsoleWorkspaceConfigOwner', { email: (workspace.owners && workspace.owners.length > 1 ? workspace.owners.map((o) => o.email || o.uid).join(', ') : workspace.ownerEmail) || '—' })} {workspace.ownerUid ? `(${workspace.ownerUid})` : ''}</p>
            <p>{t('NLSOwnerConsoleWorkspaceConfigCreated', { created: date(workspace.createdAt), updated: date(workspace.updatedAt) })}</p>
          </div>
          <section className="ownerWorkspaceDemoUnlock" aria-label={t('NLSOwnerConsoleWorkspaceDetailDemoUnlock')} style={{ display: 'grid', gap: 4, marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>{t('NLSOwnerConsoleWorkspaceDetailDemoUnlock')}</h3>
            <label>
              <input
                type="checkbox"
                role="switch"
                checked={demoUnlockEnabled}
                disabled={mutationPending || !ACTIONS.find((entry) => entry.kind === 'SET_WORKSPACE_DEMO_UNLOCK')?.roles.includes(role)}
                aria-label={demoUnlockEnabled ? t('NLSOwnerConsoleWorkspaceDetailDemoUnlockTurnOff') : t('NLSOwnerConsoleWorkspaceDetailDemoUnlockTurnOn')}
                onChange={() => changeAction('SET_WORKSPACE_DEMO_UNLOCK')}
              />{' '}
              {demoUnlockEnabled ? t('NLSOwnerConsoleWorkspaceDetailDemoUnlockOn') : t('NLSOwnerConsoleWorkspaceDetailDemoUnlockOff')}
            </label>
            <p style={{ margin: 0 }}>
              {workspace.demoUnlock?.setAt
                ? t('NLSOwnerConsoleWorkspaceDetailDemoUnlockSetBy', { user: workspace.demoUnlock.setBy || '—', date: date(workspace.demoUnlock.setAt) })
                : t('NLSOwnerConsoleWorkspaceDetailDemoUnlockNever')}
            </p>
            <p style={{ margin: 0 }}>{t('NLSOwnerConsoleWorkspaceDetailDemoUnlockHelp')}</p>
          </section>
          <section className="ownerWorkspaceDemoStory" aria-label={t('NLSOwnerConsoleWorkspaceDemoStoryTitle')} style={{ display: 'grid', gap: 4, marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>{t('NLSOwnerConsoleWorkspaceDemoStoryTitle')}</h3>
            <p style={{ margin: 0 }}>{t('NLSOwnerConsoleWorkspaceDemoStoryHelp')}</p>
            {!isFounder && <p style={{ margin: 0 }}>{t('NLSOwnerConsoleWorkspaceDemoStoryFounderOnly')}</p>}
            {isFounder && !demoUnlockEnabled && <p style={{ margin: 0 }}>{t('NLSOwnerConsoleWorkspaceDemoStoryNeedsUnlock')}</p>}
            <label>
              {t('NLSOwnerConsoleWorkspaceDemoStoryNameLabel')}
              <input
                value={storyName}
                maxLength={80}
                disabled={!isFounder || !demoUnlockEnabled || storyBusy}
                onChange={(event) => { setStoryName(event.target.value); setStoryNameError(null) }}
                autoComplete="off"
              />
            </label>
            {storyNameError && <p className="ownerWorkspaceError" role="alert" style={{ margin: 0 }}>{storyNameError}</p>}
            <div>
              <button type="button" disabled={!isFounder || !demoUnlockEnabled || storyBusy} onClick={() => openStoryAction('SAVE_DEMO_STORY')}>
                {t('NLSOwnerConsoleWorkspaceDemoStorySave')}
              </button>
            </div>
            {isFounder && !stories && !storiesFailed && <p style={{ margin: 0 }}>{t('NLSOwnerConsoleWorkspaceDemoStoryLoading')}</p>}
            {isFounder && storiesFailed && <p className="ownerWorkspaceError" role="alert" style={{ margin: 0 }}>{t('NLSOwnerConsoleWorkspaceDemoStoryLoadError')}</p>}
            {isFounder && stories && (stories.length === 0
              ? <p style={{ margin: 0 }}>{t('NLSOwnerConsoleWorkspaceDemoStoryEmpty')}</p>
              : (
                <ul className="ownerWorkspaceDetailRows">
                  {stories.map((story) => (
                    <li key={story.storyId}>
                      <span>{t('NLSOwnerConsoleWorkspaceDemoStoryRow', { name: story.name, date: date(story.createdAt), docs: number(story.totalDocs), size: storySize(story.sizeBytes) })}</span>{' '}
                      <button type="button" disabled={!demoUnlockEnabled || storyBusy} aria-label={t('NLSOwnerConsoleWorkspaceDemoStoryRestoreNamed', { name: story.name })} onClick={() => openStoryAction('RESTORE_DEMO_STORY', story)}>
                        {t('NLSOwnerConsoleWorkspaceDemoStoryRestore')}
                      </button>{' '}
                      <button type="button" disabled={storyBusy} aria-label={t('NLSOwnerConsoleWorkspaceDemoStoryDeleteNamed', { name: story.name })} onClick={() => openStoryAction('DELETE_DEMO_STORY', story)}>
                        {t('NLSOwnerConsoleWorkspaceDemoStoryDelete')}
                      </button>
                    </li>
                  ))}
                </ul>
              ))}
          </section>
          <HosPatientLegalSetupCard workspaceId={workspace.id} />
          <div className="ownerWorkspaceActions">
            <h2>{t('NLSOwnerConsoleWorkspaceDetailActions')}</h2>
            {normalActions.map((entry) => (
              <button type="button" key={entry.kind} disabled={mutationPending} onClick={() => changeAction(entry.kind)}>
                {t(entry.labelKey)}
              </button>
            ))}
          </div>
        </article>
      )}

      {tab === 'danger' && (
        <article>
          <h2>{t('NLSOwnerConsoleWorkspaceDetailDanger')}</h2>
          <div className="ownerWorkspaceActions">
            {dangerActions.map((entry) => (
              <button type="button" key={entry.kind} disabled={mutationPending} onClick={() => changeAction(entry.kind)}>
                {t(entry.labelKey)}
              </button>
            ))}
          </div>
        </article>
      )}

      {actionResult && (
        <section aria-label={t('NLSOwnerConsoleWorkspaceDetailResult')}>
          <h2>{t('NLSOwnerConsoleWorkspaceDetailResult')}</h2>
          <pre className="ownerWorkspacePreview">{JSON.stringify(actionResult, null, 2)}</pre>
        </section>
      )}

      {selected && (
        <section className="ownerWorkspaceDrawer" aria-label={`${selectedLabel} ${t('NLSOwnerConsoleWorkspaceDetailActions')}`}>
          <h2>{selectedLabel}</h2>
          <p>{t('NLSOwnerConsoleWorkspaceDetailTarget')}: <code>{workspace.id}</code></p>
          <label>
            {t('NLSOwnerConsoleWorkspaceDetailReason')}
            <select disabled={mutationPending} value={reason} onChange={(event) => changeParam(setReason, event.target.value)}>
              <option>DATA_CORRECTION</option>
              <option>SUPPORT_REQUEST</option>
              <option>SECURITY_INCIDENT</option>
            </select>
          </label>
          {action === 'GRANT_WORKSPACE_CREDITS' && (
            <label>
              {t('NLSOwnerConsoleWorkspaceDetailCredits')}
              <input
                aria-label={t('NLSOwnerConsoleWorkspaceDetailCredits')}
                type="number"
                min="1"
                max={role === 'support' ? 500 : undefined}
                disabled={mutationPending}
                value={credits}
                onChange={(event) => changeParam(setCredits, event.target.value)}
              />
            </label>
          )}
          {action === 'CHANGE_PLAN' && (
            <label>
              {t('NLSOwnerConsoleWorkspaceDetailPlan')}
              <select disabled={mutationPending} value={plan} onChange={(event) => changeParam(setPlan, event.target.value)}>
                {['FREE_TIER', 'ESSENTIAL_TIER', 'GROWTH_TIER', 'PROFESSIONAL_TIER', 'ENTERPRISE_TIER'].map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            </label>
          )}
          {action === 'SUSPEND_WORKSPACE' && (
            <label>
              <input
                type="checkbox"
                checked={suspended}
                disabled={mutationPending}
                onChange={(event) => changeParam(setSuspended, event.target.checked)}
              />{' '}
              {t('NLSOwnerConsoleWorkspaceDetailSuspendWorkspace')}
            </label>
          )}
          {action === 'SET_WORKSPACE_DEMO_UNLOCK' && (
            <p>{demoUnlockEnabled ? t('NLSOwnerConsoleWorkspaceDetailDemoUnlockTurnOff') : t('NLSOwnerConsoleWorkspaceDetailDemoUnlockTurnOn')}</p>
          )}
          {action === 'SAVE_DEMO_STORY' && <p>{storyName.trim()}</p>}
          {action === 'RESTORE_DEMO_STORY' && <p>{selectedStory?.name || storyId}</p>}
          {action === 'DELETE_DEMO_STORY' && <p>{selectedStory?.name || storyId} — {t('NLSOwnerConsoleWorkspaceDemoStoryDeleteWarning')}</p>}
          {action === 'RETRY_FAILED_JOB' && (
            <label>
              {t('NLSOwnerConsoleWorkspaceDetailFailedRun')}
              <select disabled={mutationPending} value={runId} onChange={(event) => changeParam(setRunId, event.target.value)}>
                <option value="">—</option>
                {detail.failedRuns.state === 'available' && detail.failedRuns.items.map((run) => (
                  <option key={run.id} value={run.id}>{run.id}</option>
                ))}
              </select>
            </label>
          )}
          {(selected.destructive || selected.requiresName) && (
            <label>
              {t('NLSOwnerConsoleWorkspaceDetailTypeName')}
              <input
                placeholder="…"
                value={confirm}
                disabled={mutationPending}
                onChange={(event) => changeParam(setConfirm, event.target.value, false)}
                autoComplete="off"
              />
            </label>
          )}
          {selected.preview && (
            <button
              type="button"
              disabled={previewPending || mutationPending}
              onClick={() => void run(true)}
            >
              {previewPending ? t('NLSOwnerConsoleWorkspaceDetailWorking') : t('NLSOwnerConsoleWorkspaceDetailPreview')}
            </button>
          )}
          {action === 'RESTORE_DEMO_STORY' && preview && typeof preview.shiftDays === 'number' && (
            <p>{t('NLSOwnerConsoleWorkspaceDemoStoryRestoreSummary', { name: String(preview.storyName ?? ''), days: preview.shiftDays, docs: storyDocs(preview.totalDocs), remove: storyDocs(preview.docsToRemove) })}</p>
          )}
          {preview && <pre className="ownerWorkspacePreview">{JSON.stringify(preview, null, 2)}</pre>}
          {actionError && <p className="ownerWorkspaceError" role="alert">{actionError}</p>}
          <div>
            <button ref={cancelRef} type="button" onClick={closeDrawer} disabled={mutationPending}>
              {t('NLSOwnerConsoleCancel')}
            </button>
            <button
              type="button"
              disabled={
                mutationPending ||
                previewPending ||
                (role === 'support' && action === 'GRANT_WORKSPACE_CREDITS' && Number(credits) > 500) ||
                (action === 'RETRY_FAILED_JOB' && !runId) ||
                Boolean(selected.preview && !preview?.previewToken) ||
                Boolean((selected.destructive || selected.requiresName) && confirm !== workspace.name)
              }
              onClick={() => void run(false)}
            >
              {mutationPending ? t('NLSOwnerConsoleWorkspaceDetailWorking') : t('NLSOwnerConsoleWorkspaceDetailConfirm')}
            </button>
          </div>
        </section>
      )}
    </section>
  )
}
